import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Table, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';
import { MachineService, DoorStatusHistoryEntry, ProductMap } from '../api/api_pb';
import { DoorStatusBadge } from '../components/DoorStatusBadge';
import { DateTime } from 'luxon';
import { useGlobalContext } from '../GlobalContext';

const DoorStatusHistoryPage: React.FC = () => {
  const { machineUuid, mdbItemNumber } = useParams<{ machineUuid: string; mdbItemNumber: string }>();
  const navigate = useNavigate();
  const machineClient = useClient(MachineService);
  const { MachineInfos } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [loadingProductMaps, setLoadingProductMaps] = useState(false);
  const [entries, setEntries] = useState<DoorStatusHistoryEntry[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | 'all'>('30days');

  // Vending machine and door selection
  const [selectedMachineUuid, setSelectedMachineUuid] = useState<string>(machineUuid || '');
  const [selectedMdbItemNumber, setSelectedMdbItemNumber] = useState<string>(mdbItemNumber || '');
  const [productMaps, setProductMaps] = useState<ProductMap[]>([]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const getStartTime = useCallback((): bigint => {
    const now = DateTime.now();
    switch (dateRange) {
      case '7days':
        return BigInt(Math.floor(now.minus({ days: 7 }).toSeconds()));
      case '30days':
        return BigInt(Math.floor(now.minus({ days: 30 }).toSeconds()));
      case 'all':
        return BigInt(0);
      default:
        return BigInt(0);
    }
  }, [dateRange]);

  const fetchProductMaps = useCallback(async (machineUuid: string) => {
    if (!machineUuid) return;

    setLoadingProductMaps(true);
    try {
      const response = await machineClient.listMachineProductMaps({ machineUuid });
      setProductMaps(response.productMaps);
    } catch (error) {
      console.error('Failed to fetch product maps:', error);
      toast.error('Failed to fetch doors for selected machine');
    } finally {
      setLoadingProductMaps(false);
    }
  }, [machineClient]);

  const fetchHistory = useCallback(async () => {
    if (!selectedMachineUuid) {
      return;
    }

    setLoading(true);
    try {
      const response = await machineClient.getDoorStatusHistory({
        vendingMachineUuid: selectedMachineUuid,
        mdbItemNumber: selectedMdbItemNumber !== '' ? parseInt(selectedMdbItemNumber, 10) : undefined,
        page,
        pageSize,
        startTime: getStartTime(),
        endTime: BigInt(0), // 0 means "now"
      });

      setEntries(response.entries);
      setTotalCount(response.totalCount);
      setPage(response.page);
      setPageSize(response.pageSize);
    } catch (error) {
      console.error('Failed to fetch door status history:', error);
      toast.error('Failed to fetch door history');
    } finally {
      setLoading(false);
    }
  }, [machineClient, selectedMachineUuid, selectedMdbItemNumber, page, pageSize, getStartTime]);

  // Load product maps when machine changes
  useEffect(() => {
    if (selectedMachineUuid) {
      fetchProductMaps(selectedMachineUuid);
    }
  }, [selectedMachineUuid, fetchProductMaps]);

  // Fetch history when parameters change
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Reset page when date range changes
  useEffect(() => {
    setPage(1);
  }, [dateRange]);

  // Update URL when selections change
  useEffect(() => {
    if (selectedMachineUuid && selectedMdbItemNumber) {
      navigate(`/door-status-history/${selectedMachineUuid}/${selectedMdbItemNumber}`);
    } else if (selectedMachineUuid) {
      navigate(`/door-status-history/${selectedMachineUuid}`);
    } else {
      navigate('/door-status-history');
    }
  }, [selectedMachineUuid, selectedMdbItemNumber, navigate]);

  const handleMachineChange = (newMachineUuid: string) => {
    setSelectedMachineUuid(newMachineUuid);
    setSelectedMdbItemNumber(''); // Reset door selection
    setProductMaps([]);
  };

  const handleDoorChange = (newMdbItemNumber: string) => {
    setSelectedMdbItemNumber(newMdbItemNumber);
  };

  const formatTimestamp = (timestamp: bigint): string => {
    const dt = DateTime.fromSeconds(Number(timestamp));
    return dt.toFormat('HH:mm:ss dd MMM yyyy ZZZZ');
  };

  const formatRelativeTime = (timestamp: bigint): string => {
    const dt = DateTime.fromSeconds(Number(timestamp));
    return dt.toRelative() || '';
  };

  const getDoorInfo = (mdbItemNumber: number): string => {
    const pm = productMaps.find(p => Number(p.mdbItemNumber) === mdbItemNumber);
    if (!pm) return `MDB ${mdbItemNumber}`;

    const parts = [];
    parts.push(`Door ${pm.doorIdDisplay}`);
    if (pm.product?.name) {
      parts.push(pm.product.name);
    }
    if (pm.trackableItem?.name) {
      parts.push(pm.trackableItem.name);
    }
    return parts.join(' - ');
  };

  const selectedMachine = MachineInfos.find(m => m.uuid === selectedMachineUuid);
  const selectedDoor = productMaps.find(pm => pm.mdbItemNumber.toString() === selectedMdbItemNumber);

  return (
    <div className="p-4">
      <h2 className="mb-4">Door Status History</h2>

      {/* Machine and Door Selection */}
      <div className="mb-3 d-flex gap-3 align-items-end">
        <Form.Group className="flex-fill">
          <Form.Label>Vending Machine:</Form.Label>
          <Form.Select
            value={selectedMachineUuid}
            onChange={(e) => handleMachineChange(e.target.value)}
          >
            <option value="">Select a machine...</option>
            {MachineInfos.map((machine) => (
              <option key={machine.uuid} value={machine.uuid}>
                {machine.name} ({machine.uuid})
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="flex-fill">
          <Form.Label>Door:</Form.Label>
          <Form.Select
            value={selectedMdbItemNumber}
            onChange={(e) => handleDoorChange(e.target.value)}
            disabled={!selectedMachineUuid || loadingProductMaps}
          >
            <option value="">
              {loadingProductMaps ? 'Loading doors...' : 'Select a door...'}
            </option>
            {productMaps.map((pm) => (
              <option key={pm.uuid} value={pm.mdbItemNumber.toString()}>
                Door {pm.doorIdDisplay} (MDB: {pm.mdbItemNumber.toString()})
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <Form.Label>Date Range:</Form.Label>
          <Form.Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as '7days' | '30days' | 'all')}
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="all">All time</option>
          </Form.Select>
        </Form.Group>
      </div>

      {selectedMachine && (
        <div className="mb-3 p-3 bg-light rounded">
          <strong>Viewing:</strong> {selectedMachine.name}
          {selectedDoor && ` - Door ${selectedDoor.doorIdDisplay}`}
        </div>
      )}

      {!selectedMachineUuid ? (
        <div className="alert alert-info">
          Please select a vending machine to view history.
        </div>
      ) : loading ? (
        <div className="text-center p-4">Loading...</div>
      ) : (
        <>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Status</th>
                {!selectedMdbItemNumber && <th>Door</th>}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={selectedMdbItemNumber ? 2 : 3} className="text-center">
                    No history available
                  </td>
                </tr>
              ) : (
                entries.map((entry, index) => (
                  <tr key={`${entry.timestamp}-${index}`}>
                    <td>
                      <div>{formatTimestamp(entry.timestamp)}</div>
                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {formatRelativeTime(entry.timestamp)}
                      </div>
                    </td>
                    <td>
                      <DoorStatusBadge isOpen={entry.doorStatusAfterEvent} />
                    </td>
                    {!selectedMdbItemNumber && (
                      <td>{getDoorInfo(entry.mdbItemNumber)}</td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DoorStatusHistoryPage;
